import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, type ProjectFile } from '@/api/client';
import { invalidateSummariesForProjectIds } from '@/lib/summariesBatchReload';
import { EntityFilesPopoverServer } from './EntityFilesPopoverServer';
import { getProjectFilesQueryOptions } from './projectFilesPopoverQuery';
import type { ProjectFilesPopoverServerProps } from './ProjectFilesPopover.types';

/**
 * Попап файлов проекта с сервера: загрузка, удаление, опционально выбор вложений для черновика письма
 * (универсальная `ProjectEmailModal`, в т.ч. «Проверка»).
 */
export function ProjectFilesPopoverServer({
  open,
  onClose,
  project,
  anchorEl,
  fileLinkSelection,
  typeFilterPersistence,
}: ProjectFilesPopoverServerProps) {
  const queryClient = useQueryClient();

  const opts = getProjectFilesQueryOptions(project.id);

  const onMutationsSettled = useCallback(() => {
    invalidateSummariesForProjectIds(queryClient, [project.id]);
  }, [queryClient, project.id]);

  return (
    <EntityFilesPopoverServer<ProjectFile>
      open={open}
      onClose={onClose}
      anchorEl={anchorEl}
      entityTitle={project.name || 'Проект'}
      fileLinkSelection={fileLinkSelection}
      realtimeDedupProjectId={project.id}
      fileTypeMenuEnabled
      typeFilterPersistence={typeFilterPersistence}
      filesQuery={{ queryKey: opts.queryKey, queryFn: opts.queryFn }}
      filesApi={{
        list: () => api.projectFiles.list(project.id),
        upload: (file, uploadedBy, onServerProgress, fileType, clientMetrics) =>
          api.projectFiles.upload(project.id, file, uploadedBy, onServerProgress, fileType, clientMetrics),
        update: (fileId, data) => api.projectFiles.update(project.id, fileId, data),
        delete: (fileId) => api.projectFiles.delete(project.id, fileId),
      }}
      onMutationsSettled={onMutationsSettled}
    />
  );
}
